import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Upload, Download } from 'lucide-react';

export const BulkUploadModal = ({
  open,
  onOpenChange,
  bulkUploadMode,
  setBulkUploadMode,
  csvFile,
  setCsvFile,
  bulkData,
  setBulkData,
  bulkUploading,
  onUploadCSV,
  onUploadJSON,
  onDownloadCSVTemplate,
  onDownloadJSONTemplate,
}) => {
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          setCsvFile(null);
          setBulkData('');
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Upload Partners</DialogTitle>
          <DialogDescription>
            Upload multiple entities at once via CSV file or JSON text
          </DialogDescription>
        </DialogHeader>

        <Tabs value={bulkUploadMode} onValueChange={setBulkUploadMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2" data-testid="bulk-upload-tabs">
            <TabsTrigger value="csv" data-testid="bulk-upload-tab-csv">CSV Upload</TabsTrigger>
            <TabsTrigger value="json" data-testid="bulk-upload-tab-json">JSON Paste</TabsTrigger>
          </TabsList>

          <TabsContent value="csv" className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onDownloadCSVTemplate}
                data-testid="download-csv-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download CSV Template
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="csv-file-input">CSV File</Label>
              <Input
                id="csv-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                data-testid="csv-file-input"
              />
              {csvFile && (
                <p className="text-sm text-muted-foreground" data-testid="csv-file-selected">
                  Selected: <span className="font-medium">{csvFile.name}</span> ({(csvFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Expected columns: <code>Location</code>, <code>Category</code>, <code>Tags</code>, <code>Title</code>, <code>Description</code>, <code>URL</code>, <code>Special Offer</code>
              </p>
            </div>
          </TabsContent>

          <TabsContent value="json" className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onDownloadJSONTemplate}
                data-testid="download-json-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download JSON Template
              </Button>
            </div>
            <div className="space-y-2">
              <Label>JSON Data</Label>
              <Textarea
                value={bulkData}
                onChange={(e) => setBulkData(e.target.value)}
                placeholder='[{"name": "Partner Name", "entity_type": "guide", "description": "...", "location": "Colorado"}]'
                rows={12}
                className="font-mono text-sm"
                data-testid="bulk-upload-textarea"
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="bulk-upload-cancel-btn"
          >
            Cancel
          </Button>
          {bulkUploadMode === 'csv' ? (
            <Button
              onClick={() => csvFile && onUploadCSV(csvFile)}
              disabled={!csvFile || bulkUploading}
              data-testid="upload-csv-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {bulkUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          ) : (
            <Button onClick={onUploadJSON} data-testid="upload-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload Entities
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadModal;
